import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Filter, Phone, Eye, Trash2, Download } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useDebounce, usePagination } from '../hooks/useCustom';
import Table from '../components/Table';
import Badge from '../components/Badge';
import Pagination from '../components/Pagination';
import Modal from '../components/Modal';
import { ConfirmModal } from '../components/Modal';
import { Input, Select, FormRow } from '../components/FormComponents';
import Breadcrumb from '../components/Breadcrumb';
import { formatDate, filterByMultipleFields, exportToCSV } from '../utils/helpers';

const LEAD_SOURCES = ['Walk-in', 'Instagram', 'Facebook', 'Referral', 'Website', 'Telepon', 'Pameran'];
const STATUSES = ['Hot', 'Warm', 'Cold'];
const CARS = ['Honda Brio', 'Honda WR-V', 'Honda HR-V', 'Honda CR-V', 'Honda Jazz', 'Honda City', 'Honda BR-V', 'Honda Accord', 'Honda Odyssey'];

const emptyForm = { nama: '', noHP: '', alamat: '', mobilDiminati: '', sumberLead: '', status: 'Warm' };

export default function Prospecting() {
  const { state, dispatch, showNotification } = useApp();
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const debouncedSearch = useDebounce(search, 300);

  const filtered = state.prospects.filter(p => {
    const matchSearch = filterByMultipleFields([p], debouncedSearch, ['nama', 'noHP', 'mobilDiminati', 'alamat']).length > 0;
    const matchStatus = !filterStatus || p.status === filterStatus;
    const matchSource = !filterSource || p.sumberLead === filterSource;
    return matchSearch && matchStatus && matchSource;
  });

  const { currentPage, totalPages, currentData, goToPage } = usePagination(filtered, 10);

  const openAdd = () => { setForm(emptyForm); setEditData(null); setShowModal(true); };
  const openEdit = (p) => { setForm({ ...p }); setEditData(p); setShowModal(true); };

  const handleSubmit = () => {
    if (!form.nama || !form.noHP) return showNotification('Nama dan No HP wajib diisi', 'error');
    if (editData) {
      dispatch({ type: 'UPDATE_PROSPECT', payload: form });
      showNotification('Prospek berhasil diperbarui');
    } else {
      dispatch({ type: 'ADD_PROSPECT', payload: form });
      showNotification('Prospek berhasil ditambahkan');
    }
    setShowModal(false);
  };

  const handleDelete = () => {
    dispatch({ type: 'DELETE_PROSPECT', payload: deleteId });
    showNotification('Prospek berhasil dihapus', 'error');
    setDeleteId(null);
  };

  const columns = [
    { key: 'nama', label: 'Nama', sortable: true, render: (v, row) => (
      <div>
        <p className="font-medium text-gray-800">{v}</p>
        <p className="text-xs text-gray-400">{row.alamat?.substring(0, 30)}...</p>
      </div>
    )},
    { key: 'noHP', label: 'No HP', render: v => (
      <a href={`tel:${v}`} className="flex items-center gap-1 text-blue-600 hover:underline text-sm"><Phone size={12}/>{v}</a>
    )},
    { key: 'mobilDiminati', label: 'Mobil Diminati', sortable: true },
    { key: 'sumberLead', label: 'Sumber', render: v => <Badge status={v} /> },
    { key: 'status', label: 'Status', sortable: true, render: v => <Badge status={v} /> },
    { key: 'createdAt', label: 'Tanggal', render: v => <span className="text-xs text-gray-500">{formatDate(v)}</span> },
    { key: 'actions', label: '', render: (_, row) => (
      <div className="flex items-center gap-1">
        <Link to={`/prospecting/${row.id}`} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"><Eye size={15}/></Link>
        <button onClick={() => openEdit(row)} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors text-xs font-medium">Edit</button>
        <button onClick={() => setDeleteId(row.id)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"><Trash2 size={15}/></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: 'Prospecting' }]} />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Prospecting</h1>
          <p className="text-sm text-gray-500">{filtered.length} prospek ditemukan</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => exportToCSV(state.prospects, 'prospek')} className="btn-secondary"><Download size={16}/>Export</button>
          <button onClick={openAdd} className="btn-primary"><Plus size={16}/>Tambah Prospek</button>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-3">
          <div className="flex-1 min-w-48 relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cari nama, HP, mobil..." className="form-input pl-9" />
          </div>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="form-input w-36 bg-white">
            <option value="">Semua Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)} className="form-input w-40 bg-white">
            <option value="">Semua Sumber</option>
            {LEAD_SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {(filterStatus || filterSource || search) && (
            <button onClick={() => { setFilterStatus(''); setFilterSource(''); setSearch(''); }} className="btn-secondary text-red-600">Reset</button>
          )}
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <Table columns={columns} data={currentData} emptyMessage="Tidak ada prospek ditemukan" />
        <div className="px-4 py-3 border-t border-gray-50">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={goToPage} totalItems={filtered.length} itemsPerPage={10} />
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editData ? 'Edit Prospek' : 'Tambah Prospek Baru'}
        size="md"
        footer={<>
          <button onClick={() => setShowModal(false)} className="btn-secondary">Batal</button>
          <button onClick={handleSubmit} className="btn-primary">{editData ? 'Simpan Perubahan' : 'Tambah Prospek'}</button>
        </>}
      >
        <div className="space-y-4">
          <FormRow>
            <Input label="Nama Lengkap *" value={form.nama} onChange={e => setForm({...form, nama: e.target.value})} placeholder="Masukkan nama" />
            <Input label="No HP *" value={form.noHP} onChange={e => setForm({...form, noHP: e.target.value})} placeholder="081234567890" />
          </FormRow>
          <Input label="Alamat" value={form.alamat} onChange={e => setForm({...form, alamat: e.target.value})} placeholder="Alamat lengkap" />
          <FormRow>
            <Select label="Mobil Diminati" value={form.mobilDiminati} onChange={e => setForm({...form, mobilDiminati: e.target.value})} options={CARS.map(c => ({ value: c, label: c }))} placeholder="Pilih mobil" />
            <Select label="Sumber Lead" value={form.sumberLead} onChange={e => setForm({...form, sumberLead: e.target.value})} options={LEAD_SOURCES.map(s => ({ value: s, label: s }))} placeholder="Pilih sumber" />
          </FormRow>
          <Select label="Status Lead" value={form.status} onChange={e => setForm({...form, status: e.target.value})} options={STATUSES.map(s => ({ value: s, label: s }))} />
        </div>
      </Modal>

      <ConfirmModal
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Hapus Prospek"
        message="Apakah Anda yakin ingin menghapus prospek ini? Data yang dihapus tidak dapat dikembalikan."
        confirmLabel="Hapus"
        confirmClass="btn-danger"
      />
    </div>
  );
}
